import { useState } from 'react';
import { Button } from '@/shared/ui/button';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import api from '@/shared/api/client';

/**
 * Etape 5 du wizard installateur : la signature est acquise, et le chantier se clôt ici.
 *
 * ⚠️ ELLE PORTE LA CLÔTURE DEPUIS LE 03/09/2026, ET CE N'EST PAS UN DÉPLACEMENT
 * COSMÉTIQUE.
 *
 * Le bouton vivait sur `StepAttenteSignature`, l'écran où le poseur regardait passer le
 * temps pendant que son client signait à distance chez Yousign. Cet écran a disparu avec
 * le canal. Sans ce bouton ici, plus aucun chantier ne pourrait être clôturé depuis le
 * parcours : `cloture.obstacles` n'a qu'un gardien, mais il faut encore que quelqu'un
 * pousse la porte.
 *
 * ⚠️ ET ELLE RESTE UN ACTE EXPLICITE. Clôturer automatiquement au retour du serveur
 * aurait été plus court d'un clic. C'est l'arbitrage du lot 4 qui l'interdit : le poseur
 * est encore sur place, et un chantier qui se ferme tout seul ne lui laisse aucun moment
 * pour constater ce qu'il emporte — en particulier un matériel resté muet, que le dossier
 * signale sans le bloquer depuis le 30/08.
 *
 * Le refus du serveur (409) NOMME ce qui bloque, et on l'affiche tel quel : « le chantier
 * n'a pas pu être clôturé » sans motif renverrait le poseur à son écran sans recours.
 */
export default function StepConfirmation({
  client, enabledEquipements, referenceChantier, resetForm, mandatId, signature,
}) {
  const [occupe, setOccupe] = useState(false);
  const [cloture, setCloture] = useState(false);
  const [message, setMessage] = useState('');

  // La signature a-t-elle abouti ? Un mandat non probant n'interdit pas de finir le
  // chantier, mais le poseur doit savoir qu'il repart avec un dossier à rattraper.
  const signee = signature?.statut === 'signe';

  const cloturer = async () => {
    setOccupe(true);
    setMessage('');
    try {
      await api.post(`/installateur/chantier/${mandatId}/cloturer`, {});
      setCloture(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const obstacles = detail?.obstacles?.map(o => o.message).join(' ');
      setMessage(obstacles || detail?.message || detail
                 || "Le chantier n'a pas pu être clôturé.");
    }
    setOccupe(false);
  };

  return (
    <div className="space-y-5 animate-fade-in text-center pt-8" data-testid="step-confirmation">
      <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
      <div>
        <h2 className="text-xl font-semibold text-neutral-900">Signature terminée</h2>
      </div>

      <div className="rounded-lg border border-neutral-200 bg-white p-4 text-left text-[12px] space-y-1.5">
        <p><span className="text-neutral-400">Client :</span> {client.civilite} {client.prenom} {client.nom}</p>
        <p><span className="text-neutral-400">Équipements :</span> {enabledEquipements.map(([t]) => t).join(', ') || '-'}</p>
        <p><span className="text-neutral-400">Réf. chantier :</span> {referenceChantier || '-'}</p>
      </div>

      {/* Le mandat existe même quand sa signature n'a pas pu se conclure. Le dire ici,
          et pas seulement dans un journal : c'est le seul moment où quelqu'un regarde. */}
      {mandatId && !signee && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-left text-[12px] text-amber-800 flex gap-2"
             data-testid="confirmation-signature-incomplete">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            Le mandat est enregistré mais sa signature n&apos;a pas pu être conclue. Le
            dossier se rattrape depuis la fiche client.
          </span>
        </div>
      )}

      {mandatId && !cloture && (
        <Button className="w-full h-12 text-[15px]" onClick={cloturer} disabled={occupe}
                data-testid="confirmation-cloturer">
          {occupe
            ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Clôture…</>
            : 'Clôturer le chantier'}
        </Button>
      )}

      {cloture && (
        <p className="text-[13px] text-emerald-600 font-medium" data-testid="confirmation-cloture-ok">
          Chantier clôturé.
        </p>
      )}

      {message && (
        <p className="text-[12px] text-red-600" data-testid="confirmation-cloture-erreur">{message}</p>
      )}

      <Button variant={cloture ? 'default' : 'outline'} className="w-full h-12 text-[15px]"
              onClick={resetForm} data-testid="new-installation-btn">
        Nouvelle installation
      </Button>
    </div>
  );
}
